import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Cta } from "../components/Cta";
import { Section } from "../components/Section";
import { AGE_GATE } from "../content/compliance";
import { TERMS_VERSION } from "../content/legal";
import { useAuth } from "../features/auth/context";
import { meKey } from "../features/catalogue/queries";
import { ConsentNotice } from "../features/legal/ConsentNotice";
import { OffshoreRefusal, UnderAgeRefusal } from "../features/legal/Refusals";
import { isApiError } from "../services/api/errors";
import { recordOnboarding } from "../services/api/onboarding";
import { usePageMeta } from "../lib/usePageMeta";
import { COUNTRIES } from "../content/countries";
import styles from "./Welcome.module.css";

/**
 * /welcome — the onboarding gate.
 *
 * ## Why a page exists at all when /signup already takes consent
 *
 * Because an account can be created without ever loading /signup. Cognito's
 * sign-in page carries a "New user? Create an account" link to its own sign-up
 * form, and that URL can be bookmarked. Managed Login's branding is visual only
 * — colours, radii, images; no text, no links — so that route cannot be closed
 * from the front door. RequireOnboarded sends anyone who arrived that way here,
 * and this is where they give what /signup would have collected.
 *
 * It is also where a TERMS_VERSION bump lands: the guard compares the version on
 * the consent row against the one this bundle is running, so changing the
 * document re-prompts everyone on their next visit. That is what makes Terms
 * §180's "reasonable notice before it takes effect" a mechanism.
 *
 * ⚠️ AND THAT ARRIVAL IS NOT YET FIT FOR A RETURNING ACCOUNT. This page is
 * written for someone who has just signed up: it is headed "Finish setting up",
 * it promises "this is the only time you'll be asked", and all four fields start
 * empty and required. Someone sent here by a version bump has already answered
 * all of it — and cannot be shown their own date of birth, because
 * `profileSchema` deliberately does not return `birthdate`. So they retype it
 * from memory, the server does the age arithmetic on what they type, and a
 * mistyped year answers 403 under_18 and locks a paying account out.
 *
 * The first bump (2026-08-26) was done while the only stale accounts were test
 * ones, precisely so this path could be found before it mattered. It is now
 * found. Do not bump TERMS_VERSION again with real customers on the other side
 * until a consent-only re-acceptance exists — see LAUNCH.md.
 *
 * ## It replaces /signup/age, and asks a different question
 *
 * The old gate asked "are you 18 or over?" and believed the answer — a
 * self-declaration has nothing to verify, which is why it had no false path.
 * This asks for a date, and the SERVER does the arithmetic: a client that
 * computes its own eligibility is a client that can claim it. A refusal comes
 * back 403 `under_18`, and the server writes nothing at all when it refuses —
 * no row, no attribute — because recording the attempt would mean storing a
 * minor's date of birth in order to remember not to serve them.
 *
 * ## The submit ORDER is the design, not an implementation detail
 *
 * It is AgeGate's proven sequence, and AgeGate.test.tsx asserted the SEQUENCE
 * rather than the outcome for a reason: the original version wrote a flag into
 * the mock database from the component, which worked in dev and did nothing
 * anywhere else — the write went to a store nothing read, and the guard bounced
 * the user back here forever.
 */
export function Welcome() {
  usePageMeta("welcome");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refreshSession, markAdultDeclared, session } = useAuth();

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [country, setCountry] = useState("AU");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  /* Separate states from `failed`, because neither is an error the user can act
     on by trying again — each is an answer, and the retry `failed` implies is
     one the server will refuse every time. */
  const [declined, setDeclined] = useState(false);
  const [offshore, setOffshore] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFailed(null);
    try {
      // 1. The server records it, and only this step is allowed to stop them.
      //    Everything after is about making the answer visible to the app.
      await recordOnboarding({
        givenName: givenName.trim(),
        familyName: familyName.trim(),
        birthdate,
        country,
        termsVersion: TERMS_VERSION,
      });
    } catch (err) {
      if (isApiError(err) && err.code === "under-age") {
        setDeclined(true);
        setBusy(false);
        return;
      }
      if (isApiError(err) && err.code === "country-unsupported") {
        setOffshore(true);
        setBusy(false);
        return;
      }
      // The real reason, not a house message. The generic version cost two
      // debugging rounds on the old gate: a client-side JSON parse failure and
      // a genuine server error read identically, and neither was what the text
      // claimed.
      setFailed(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
      return;
    }

    // 2. Confirmed. From here they get through regardless of what the token
    //    does — see AuthProvider on why the claim alone is not enough.
    markAdultDeclared();

    // 3. Best-effort: pick up the real custom:dobVerified claim so a reload
    //    doesn't need step 2. A failure here is NOT a reason to block someone
    //    the server has already recorded, so it is tolerated deliberately
    //    rather than caught by accident.
    //
    //    THE RETURN VALUE MATTERS AS MUCH AS THE EXCEPTION. `refreshSession`
    //    does not throw when the refresh token is gone or rejected — the
    //    adapter clears the tokens and returns null (cognitoAdapter.ts:288-293),
    //    and AuthProvider then sets status to "anonymous". A version of this
    //    that only caught the throw sailed past that and navigated to /app,
    //    where RequireAuth bounced a user who had JUST onboarded straight to
    //    /login. The catch never ran, so nothing looked wrong.
    let stillSignedIn = true;
    try {
      stillSignedIn = Boolean(await refreshSession());
    } catch {
      /* keep going — the in-memory declaration carries this session */
    }

    // 4. The guard reads consent off the ["me"] query. Without this it re-reads
    //    the cached pre-onboarding answer and bounces them straight back here.
    await qc.invalidateQueries({ queryKey: meKey(session?.userId) });

    if (!stillSignedIn) {
      // The session went with the refresh. Their consent IS recorded, so this
      // is not a failure of onboarding — send them to sign in again rather than
      // to /app, which would bounce them there anyway and look like a loop.
      navigate("/login", { replace: true, state: { from: "/app" } });
      return;
    }

    navigate("/app", { replace: true });
  }

  /* Both refusals live in features/legal/Refusals — /signup renders the same
     two, from the same server rule (kev_eligibility), and one decision
     explained two different ways depending on which door you came through is
     how copy drifts. */
  if (offshore) return <OffshoreRefusal />;
  if (declined) return <UnderAgeRefusal />;

  return (
    <Section eyebrow="One thing first" heading="Finish setting up" tone="ink" level={1}>
      <div className={styles.wrap}>
        <form className={styles.form} onSubmit={onSubmit}>
          <p className={styles.hint}>
            KEV needs a few details before it can get started. This is the only time
            you&rsquo;ll be asked.
          </p>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="givenName">
              First name
            </label>
            <input
              id="givenName"
              className={styles.input}
              type="text"
              autoComplete="given-name"
              required
              maxLength={64}
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
            />
            <p className={styles.note}>So Kev knows what to call you.</p>
          </div>

          {/* Full name, at Ned's direction 2026-08-07. The first draft took a
              first name only, on the Privacy Act's collection limb — do not
              collect what you cannot state a use for. The stated use is account
              identification for a product that takes payments and must be able
              to match an account to a person on a support or dispute request,
              which is a use. Recorded here because the reasoning against was
              real and should not have to be rediscovered. */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="familyName">
              Last name
            </label>
            <input
              id="familyName"
              className={styles.input}
              type="text"
              autoComplete="family-name"
              required
              maxLength={64}
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="birthdate">
              Date of birth
            </label>
            <input
              id="birthdate"
              className={styles.input}
              type="date"
              autoComplete="bday"
              required
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
            />
            <p className={styles.note}>KEV is {AGE_GATE} only — fantasy included.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="country">
              Country of residence
            </label>
            <select
              id="country"
              className={styles.input}
              autoComplete="country"
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {failed && (
            <p role="alert" className={styles.error}>
              Couldn&rsquo;t save that just now. Have another go. You stay
              on this page until it saves, which is deliberate.
              <br />
              <span className={styles.errorDetail}>{failed}</span>
            </p>
          )}

          <Cta type="submit" disabled={busy}>
            {busy ? "One sec…" : "Continue"}
          </Cta>

          {/* Same block, same wording, same source as /signup — see ConsentNotice
              on why there is exactly one copy of it. */}
          <ConsentNotice />
        </form>
      </div>
    </Section>
  );
}
